import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import Sticky from 'react-stickynode';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { BasicModal } from '../../styles/modal';
import { Component as ByoImageProgressBar } from '../ByoImageProgressBar';
import { useByoContext } from '../ByoProvider';
import ToastMessages from '../ToastMessages/ToastMessages';

const TOP_BAR_EXIT_DURATION = 1000;
const TOP_BAR_EXIT_DELAY = 1000;
const MODAL_DELAY = 1500;

const dialogStyle = css`
  width: 100%;
  border-radius: 0 0 8px 8px;
  box-shadow: 0px -2px 8px 0px ${({ theme }) => theme.colors.lightShadow1};
`;

const ProgressBarModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionDelay: MODAL_DELAY,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
})`
  ${BasicModal.Wrapper} {
    display: flex;
    align-items: flex-start;
  }
`;

const ModalTitleContainer = styled.div``;

const StyledSticky = styled(Sticky)`
  & > .sticky-inner-wrapper {
    z-index: 2;
  }
`;

const MobileLayoutContainer = styled.div`
  border-radius: 0px 0px 4px 4px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 4px ${({ theme }) => theme.colors.lightShadow1};

  &.bar-exit {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  &.bar-exit-active {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
    transition: opacity ${TOP_BAR_EXIT_DURATION}ms,
      transform ${TOP_BAR_EXIT_DURATION}ms linear;
    transition-delay: ${TOP_BAR_EXIT_DELAY}ms;
  }
`;

const MobileLayoutOne = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.moderate}px;
`;

const MobileLayoutTwo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  padding: ${({ theme }) => theme.spacing.tiny}px
    ${({ theme }) => theme.spacing.moderate}px
    ${({ theme }) => theme.spacing.tiny}px
    ${({ theme }) => theme.spacing.tiny}px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.tiny}px;
`;

const ImageBarContainer = styled.div`
  margin: ${({ theme }) => theme.spacing.small}px 0;
`;

export default function ByoStickyProgressBarMobile({
  buildYourSetTitle,
  closeButton,
  confirmationText,
  guideText,
  onExitButtonClick,
  interstitialTitleText,
  isGridPage,
  modalCTA,
}) {
  const { isSetCompleted, isProgressBarModalOpen, isExitModalOpen } =
    useByoContext();

  const progressBarModalForMobile = useMemo(
    () => (
      <ProgressBarModal
        isOpen={isProgressBarModalOpen}
        onExit={onExitButtonClick}
        overlayClickExits={false}
        titleId="progress_bar_modal_mobile"
      >
        <MobileLayoutOne>
          <ModalTitleContainer id="progress_bar_modal_mobile">
            {buildYourSetTitle}
          </ModalTitleContainer>
          <ImageBarContainer>
            <ByoImageProgressBar imageWidth={80} isTextVisible />
          </ImageBarContainer>
          {guideText}
          {modalCTA}
        </MobileLayoutOne>
      </ProgressBarModal>
    ),
    [
      buildYourSetTitle,
      guideText,
      onExitButtonClick,
      isProgressBarModalOpen,
      modalCTA,
    ]
  );

  const progressBarForMobile = useMemo(
    () =>
      isGridPage ? (
        <MobileLayoutOne>
          {closeButton}
          {interstitialTitleText}
          {guideText}
          <ImageBarContainer>
            <ByoImageProgressBar imageWidth={80} isTextVisible />
          </ImageBarContainer>
        </MobileLayoutOne>
      ) : (
        <MobileLayoutTwo>
          {closeButton}
          <ByoImageProgressBar imageWidth={40} />
          <TextContainer>
            {isProgressBarModalOpen || isSetCompleted
              ? confirmationText
              : guideText}
          </TextContainer>
          {/* when exit modal shows up, we want to hide progress bar modal to avoid multiple modal show at the same time */}
          {isExitModalOpen ? null : progressBarModalForMobile}
        </MobileLayoutTwo>
      ),
    [
      isGridPage,
      closeButton,
      guideText,
      interstitialTitleText,
      isProgressBarModalOpen,
      isSetCompleted,
      confirmationText,
      isExitModalOpen,
      progressBarModalForMobile,
    ]
  );

  return (
    <StyledSticky>
      <CSSTransition
        classNames="bar"
        in={!isSetCompleted}
        timeout={TOP_BAR_EXIT_DURATION}
        unmountOnExit
      >
        <MobileLayoutContainer data-autotag="sticky_progress_bar_mobile">
          {progressBarForMobile}
          <ToastMessages />
        </MobileLayoutContainer>
      </CSSTransition>
    </StyledSticky>
  );
}

ByoStickyProgressBarMobile.propTypes = {
  buildYourSetTitle: PropTypes.node,
  closeButton: PropTypes.node,
  confirmationText: PropTypes.node,
  guideText: PropTypes.node,
  onExitButtonClick: PropTypes.func,
  interstitialTitleText: PropTypes.node,
  isGridPage: PropTypes.bool,
  modalCTA: PropTypes.node,
};
